import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { grey, orange, red, yellow, blue } from '@mui/material/colors'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useState } from 'react'

const TEXT_DARK = 'rgba(0, 0, 0, 0.87)'

/* ─── Single stat cell ─────────────────────────────────────────────────────── */
const StatCell = ({ label, value, icon: Icon, iconColor, isLast, compact }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: compact ? 56 : 80,
      px: compact ? 0.75 : 1,
      py: compact ? 0.4 : 0.5,
      borderRight: isLast ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
      textAlign: 'center',
      flexShrink: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.2 }}>
      <Typography
        variant='caption'
        sx={{
          color: TEXT_DARK,
          fontWeight: 600,
          fontSize: compact ? '0.55rem' : '0.62rem',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Icon sx={{ fontSize: compact ? 11 : 13, color: iconColor, flexShrink: 0 }} />
    </Box>
    <Typography
      component='span'
      sx={{
        fontWeight: 700,
        lineHeight: 1.15,
        color: TEXT_DARK,
        fontSize: compact ? '0.78rem' : '0.88rem',
      }}
    >
      {value}
    </Typography>
  </Box>
)

/* ─── Vertical group label (desktop) ──────────────────────────────────────── */
const GroupLabel = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 0.75,
      borderRight: (theme) => `1px solid ${theme.palette.divider}`,
      writingMode: 'vertical-rl',
      textOrientation: 'mixed',
      transform: 'rotate(180deg)',
    }}
  >
    <Typography
      variant='caption'
      sx={{
        color: TEXT_DARK,
        fontWeight: 700,
        fontSize: '0.6rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  </Box>
)

/* ─── Horizontal group label (mobile/tablet) ───────────────────────────────── */
const RowGroupLabel = ({ children }) => (
  <Box
    sx={{
      px: 0.75,
      py: 0.25,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      background: 'rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Typography
      variant='caption'
      sx={{
        color: TEXT_DARK,
        fontWeight: 700,
        fontSize: '0.58rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  </Box>
)

/* ─── One scrollable row of cells ──────────────────────────────────────────── */
const CellRow = ({ rows, data, compact }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: 2 },
    }}
  >
    {rows.map((row, i) => (
      <StatCell
        key={row.key}
        label={row.label}
        value={data[row.key] ?? 0}
        icon={row.icon}
        iconColor={row.color}
        isLast={i === rows.length - 1}
        compact={compact}
      />
    ))}
  </Box>
)

/* ─── Pill shown when collapsed (mobile) ───────────────────────────────────── */
const CollapsedPill = ({ total, totalPeople, onExpand }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.6,
      cursor: 'pointer',
    }}
    onClick={onExpand}
  >
    <DirectionsCarIcon sx={{ fontSize: 15, color: grey[700] }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: TEXT_DARK }}>{total}</Typography>
    <Box sx={{ width: 1, height: 20, background: (theme) => theme.palette.divider }} />
    <PeopleIcon sx={{ fontSize: 15, color: blue[700] }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: TEXT_DARK }}>{totalPeople}</Typography>
    <ExpandMoreIcon sx={{ fontSize: 16, color: grey[600], ml: 0.5 }} />
  </Box>
)

/* ─── Main component ────────────────────────────────────────────────────────── */
export default function GeneralInfo({ crashesSummary }) {
  const { _ } = useLingui()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isTablet  = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  const [expanded, setExpanded] = useState(false)

  const s = crashesSummary ?? {}

  const crashRows = [
    { key: 'total',                label: _(msg`Total`),   icon: DirectionsCarIcon, color: grey[700] },
    { key: 'slightInjurySeverity', label: _(msg`Slight`),  icon: DirectionsCarIcon, color: yellow.A700 },
    { key: 'seriousInjurySeverity',label: _(msg`Serious`), icon: DirectionsCarIcon, color: orange[800] },
    { key: 'fatalSeverity',        label: _(msg`Fatal`),   icon: DirectionsCarIcon, color: red.A700 },
  ]

  const peopleRows = [
    { key: 'totalPeople',              label: _(msg`Total`),   icon: PeopleIcon,  color: blue[700] },
    { key: 'slightInjurySeverityPeople', label: _(msg`Slight`),icon: PersonIcon,  color: yellow.A700 },
    { key: 'seriousInjurySeverityPeople',label: _(msg`Serious`),icon: PersonIcon, color: orange[800] },
    { key: 'fatalSeverityPeople',      label: _(msg`Fatal`),   icon: PersonIcon,  color: red.A700 },
  ]

  const containerBase = {
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)',
    border: (theme) => `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
    position: 'absolute',
    left: '50%',
    bottom: '1.5rem',
    transform: 'translateX(-50%)',
    zIndex: (theme) => theme.zIndex.drawer - 1,
    overflow: 'hidden',
  }

  /* ── Desktop: single horizontal row ─────────────────────────────────────── */
  if (isDesktop) {
    return (
      <Box sx={{ ...containerBase, display: 'inline-flex', alignItems: 'stretch' }}>
        <GroupLabel>{_(msg`Crashes`)}</GroupLabel>
        {crashRows.map((row) => (
          <StatCell key={row.key} label={row.label} value={s[row.key] ?? 0}
            icon={row.icon} iconColor={row.color} isLast={false} />
        ))}
        <Box sx={{ width: 3, background: (theme) => theme.palette.divider, flexShrink: 0 }} />
        <GroupLabel>{_(msg`People`)}</GroupLabel>
        {peopleRows.map((row, i) => (
          <StatCell key={row.key} label={row.label} value={s[row.key] ?? 0}
            icon={row.icon} iconColor={row.color} isLast={i === peopleRows.length - 1} />
        ))}
      </Box>
    )
  }

  /* ── Tablet: two stacked scrollable rows ─────────────────────────────────── */
  if (isTablet) {
    return (
      <Box sx={{ ...containerBase, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 'calc(100vw - 2rem)' }}>
        <RowGroupLabel>{_(msg`Crashes`)}</RowGroupLabel>
        <CellRow rows={crashRows} data={s} compact />
        <Box sx={{ height: 2, background: (theme) => theme.palette.divider }} />
        <RowGroupLabel>{_(msg`People`)}</RowGroupLabel>
        <CellRow rows={peopleRows} data={s} compact />
      </Box>
    )
  }

  /* ── Mobile: collapsed pill → expand to stacked rows ────────────────────── */
  return (
    <Box sx={{ ...containerBase, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 'calc(100vw - 2rem)' }}>
      {expanded ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1, pt: 0.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: TEXT_DARK,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {_(msg`Summary`)}
            </Typography>
            <IconButton size='small' onClick={() => setExpanded(false)} sx={{ p: 0.25 }}>
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <RowGroupLabel>{_(msg`Crashes`)}</RowGroupLabel>
          <CellRow rows={crashRows} data={s} compact />
          <Box sx={{ height: 2, background: (theme) => theme.palette.divider }} />
          <RowGroupLabel>{_(msg`People`)}</RowGroupLabel>
          <CellRow rows={peopleRows} data={s} compact />
        </>
      ) : (
        <CollapsedPill
          total={s.total ?? 0}
          totalPeople={s.totalPeople ?? 0}
          onExpand={() => setExpanded(true)}
        />
      )}
    </Box>
  )
}
